import { engineComponent } from "neo-common";
import { BaseController } from "./BaseController";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components/Effects/Tweens";
import { ModuleManager } from "../../Managers/ModuleManager";
import { DataManager } from "../../Managers/DataManager";
import { CustomUtils } from "../../Utils/CustomUtils";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { FreespinsView } from "../Freespins/FreespinsView";
import { PaylinesExtended } from "../../CustomComponents/PaylinesExtended";
import { LayoutManager } from "../../Managers/LayoutManager";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { ClockSelector } from "../../CustomComponents/ClockSelector";
import { Communication } from "neo-games";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { TimelineElement } from "../../Components/Elements/Graphics/TimelineElement";
import { GroupTweens } from "../../CustomComponents/GroupTweens";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutContainerElement {
    public static LayoutManager: LayoutManager;

    static FsMessageBox: SwitchElement;

    private _wasFinished: boolean = false;
    private _controller: BaseController;
    private finallyFinishTimer: FrameTimerEventEmitter;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _outroDelayFinally: FrameTimerEventEmitter;
    private _paylines: PaylinesExtended;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;

    private bonusSelector: ClockSelector;
    private fsMessage: TimelineElement;

    private groupTweens: SwitchElement;
    private groupTweensPortrait: SwitchElement;

    constructor() {
        super();
        this.finallyFinishTimer = CreateFrameTimer("finallyFinishTimer");
        this.finallyFinishTimer.on(FrameTimerEvents.Complete, this.finallyFinishWinAnimator.bind(this));
        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));
        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));
        this._outroDelayFinally = CreateFrameTimer("outroFinally");
        this._outroDelayFinally.on(FrameTimerEvents.Complete, this.outroFinally.bind(this));
    }

    public create(node: Element): void {
        super.create(node);

        BaseView.LayoutManager = new LayoutManager();

        if (this.childs["Symbols"] != null) {
            this._symbols = this.childs["Symbols"];
            this.removeChild(this._symbols);
        }
        if (this.childs["Paylines"] != null) this._paylines = this.childs["Paylines"];
        if (this.childs["Machine"] != null) this._machine = this.childs["Machine"];
        if (this.childs["WinAnimator"] != null) this._winAnimator = this.childs["WinAnimator"];
        if (this.childs["FsMessageBox"] != null) BaseView.FsMessageBox = this.childs["FsMessageBox"];
        if (this.childs["GroupTweens"] != null) this.groupTweens = this.childs["GroupTweens"];
        if (this.childs["GroupTweensPortrait"] != null) this.groupTweensPortrait = this.childs["GroupTweensPortrait"];
        if (this.childs["FSMessage"] != null) {
            this.fsMessage = this.childs["FSMessage"];
            this.fsMessage.visible = false;
        }

        for (let key in this.childs) {
            const item = this.childs[key];
            if (item instanceof ClockSelector) {
                this.bonusSelector = item;
                this.bonusSelector.on(
                    ClockSelector.SelectionFinished,
                    function () {
                        this.finallyFinishWinAnimator();
                    }.bind(this),
                );
            }
        }

        // set childs to GroupTweens object
        if (this.groupTweens) {
            let groupTweenElements: Object = this.groupTweens.getChildElements();
            if (groupTweenElements) {
                for (let key in groupTweenElements) {
                    if (groupTweenElements.hasOwnProperty(key)) {
                        if (groupTweenElements[key] instanceof GroupTweens) {
                            (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                        }
                    }
                }
            }
        }
        // set childs to GroupTweens object
        if (this.groupTweensPortrait) {
            let groupTweenElements: Object = this.groupTweensPortrait.getChildElements();
            if (groupTweenElements) {
                for (let key in groupTweenElements) {
                    if (groupTweenElements.hasOwnProperty(key)) {
                        if (groupTweenElements[key] instanceof GroupTweens) {
                            (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                        }
                    }
                }
            }
        }
    }

    public initReceived(controller: BaseController): void {
        this.renderable = !ModuleManager.freespinsActive();

        for (let key in this.childs) {
            if (key !== "Background" && key !== "MachineMask") {
                Tweens.removeTweens(this.childs[key]);
                this.childs[key].alpha = 0;
            }
        }
        this.childs["Background"].alpha = 0.6;

        this._controller = controller;

        PlayList.loop("SND_BaseLoop");
        PlayList.loop("SND_FSLoop");
        if (ModuleManager.freespinsActive()) {
            PlayList.setVolume("SND_BaseLoop", 0);
        } else {
            PlayList.setVolume("SND_FSLoop", 0);
        }

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._winAnimator,
                            null,
                            this.childs["Teasing"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["NoWinContainer"],
                                this.childs["WinContainer"],
                                this,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }
    }

    public spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    public spin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop", 30);

        AmbientBackground.changeAmbientBackground("base", 30);

        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
    }

    public stop(): void {
        this._machine.spinStop(null, true);
    }

    public collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop", 30);

        AmbientBackground.changeAmbientBackground("base", 30);
        this._winAnimator.reset(true);
    }

    public initIntro(): void {
        this.renderable = true;

        for (let key in this.childs) {
            if (key !== "Background" && key !== "MachineMask") {
                Tweens.removeTweens(this.childs[key]);
                this.childs[key].alpha = 1;
            }
        }
        this.childs["Background"].alpha = 1;

        if (ModuleManager.freespinsEnabled()) {
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.play("SND_BaseMessage", 0);
            PlayList.loop("SND_FSWait", 30);
        } else if (GameManager.gameResponse.totalWin > 0) {
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.resetVolume("SND_BaseLoop", 30);
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);

        /*
        if(ModuleManager.freespinsEnabled()) {
            let filtered = GameManager.baseResponse.wins
                .filter(win => win.trigger === Communication.Game.WinTrigger.Scatter || win.trigger === Communication.Game.WinTrigger.Custom);
            if(filtered.length) {
                let total = 0;
                filtered.forEach(win => {
                    if(win.customData && win.customData.multiplier) {
                        total += win.customData.multiplier;
                    }
                });
                let mul = GameManager.freespinsResponse.customData ? GameManager.freespinsResponse.customData.currentWildMultiplier : -1;

                this.bonusSelector.awaitOutro({
                    multiplier: (total ? total : mul),
                    freespins: GameManager.freespinsResponse.freeSpins.remainingCount,
                    symbols: this._winAnimator.currentSymbols
                });
            }
        }
         */
    }

    public introPre(): void {
        this.renderable = true;
    }

    public intro(long: boolean = false): void {
        this.renderable = true;
        this.initBaseLoop();

        for (let key in this.childs) {
            if (key !== "Background" && key !== "MachineMask") {
                Tweens.removeTweens(this.childs[key]);
                Tweens.alphaTween(this.childs[key], {
                    alphas: [0, 1],
                    tweenDuration: 10,
                });
            }
        }
        Tweens.removeTweens(this.childs["Background"]);
        Tweens.alphaTween(this.childs["Background"], {
            alphas: [0.6, 1],
            tweenDuration: 10,
        });

        this._winAnimator.currentSymbols.forEach((reel) => {
            reel.forEach((symbol) => {
                symbol.visible = true;
            });
        });

        this.visible = true;

        if (BaseView.FsMessageBox) {
            BaseView.FsMessageBox.goToCase("EMPTY");
        }
        FreespinsView.Multiplier = 0;

        if (this.childs["Paylines"] != null) {
            Tweens.removeTweens(this.childs["Paylines"]);
            this.childs["Paylines"].alpha = 1;
        }

        if (GameManager.baseResponse && GameManager.baseResponse.wins && GameManager.baseResponse.wins.length > 0) {
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.setVolume("SND_FSLoop", 0);
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.resetVolume("SND_BaseLoop", 30);
        }

        if (LayoutManager.isPortrait) {
            this.groupTweensPortrait.goToCase(GroupTweens.CaseGambleToBase);
        } else {
            this.groupTweens.goToCase(GroupTweens.CaseGambleToBase);
        }

        if (long) {
            this._introDelay.start(30);
        } else {
            this._introDelay.start(30);
        }
    }

    public outro(): void {
        PlayList.setVolume("SND_BaseLoop", 0);
        PlayList.stop("SND_BaseRecapLoop");

        this.visible = true;
        Tweens.removeTweens(this);

        let wasFinished: boolean = this._wasFinished;

        if (ModuleManager.freespinsActive()) {
            AmbientBackground.changeAmbientBackground("fs", 120);

            this.fsMessage.goToOutro();

            //remove outro tween
            if (this.childs["Paylines"] != null) {
                Tweens.removeTweens(this.childs["Paylines"]);
                this.childs["Paylines"].alpha = 0;
            }

            PlayList.stop("SND_FSWait", 30);
            //PlayList.play('SND_FSTransIn');

            this._outroDelay.start(30);
        } else {
            PlayList.play("SND_GambleTransition");
            PlayList.play("SND_FSTransIn");
            if (wasFinished) {
                this._outroDelay.start(60);
            } else {
                this._outroDelay.start(90);
            }
        }
    }

    public disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    public resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    public disableBase(): void {
        this._winAnimator.resetTimer();
    }

    public selectPayline(): void {}

    public toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
            PlayList.resetVolume("SND_BaseLoop");
        } else {
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.stop("SND_BaseRecapLoop");
        }
    }

    private initBaseLoop(): void {
        PlayList.loop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");
    }

    private onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    private machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.setVolume("SND_BaseLoop", 0);
        }
    }

    private finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    private finishWinAnimator(afterBonusGame: boolean = false): void {
        let filtered = GameManager.baseResponse.wins.filter(
            (win) =>
                win.trigger === Communication.Game.WinTrigger.Scatter ||
                win.trigger === Communication.Game.WinTrigger.Custom,
        );
        if (filtered.length) {
            this.finallyFinishTimer.start(GameManager.baseResponse.currentWin ? 0 : 120);
        } else {
            this._winAnimator.cycleWins(true);
            this.finallyFinishWinAnimator(afterBonusGame);
        }
    }

    private finallyFinishWinAnimator(afterBonusGame: boolean = false): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            if (ModuleManager.freespinsEnabled()) {
                PlayList.setVolume("SND_BaseLoop", 0);
                PlayList.play("SND_BaseMessage", 0);
                PlayList.loop("SND_FSWait", 30);
            }
        }

        this._controller.allWinsFinished();
    }

    private introFinished(): void {
        this._controller.introFinished();
    }

    private outroFinished(): void {
        if (!ModuleManager.freespinsEnabled()) {
            //PlayList.stop('SND_FSWait', 30);

            for (let key in this.childs) {
                if (key !== "Background" && key !== "MachineMask") {
                    Tweens.removeTweens(this.childs[key]);
                    Tweens.alphaTween(this.childs[key], {
                        alphas: [1, 0],
                        tweenDuration: 15,
                        tweenDelay: 30,
                    });
                }
            }
            Tweens.removeTweens(this.childs["Background"]);
            Tweens.alphaTween(this.childs["Background"], {
                alphas: [1, 0.6],
                tweenDuration: 15,
                tweenDelay: 30,
            });

            if (LayoutManager.isPortrait) {
                this.groupTweensPortrait.goToCase(GroupTweens.CaseBaseToGamble);
            } else {
                this.groupTweens.goToCase(GroupTweens.CaseBaseToGamble);
            }
        }

        this._controller.outroFinished();
        this._winAnimator.resetTimer();
        this._outroDelayFinally.start(60);
    }

    private outroFinally(): void {
        this._winAnimator.reset(true);
        if (BaseView.FsMessageBox) {
            BaseView.FsMessageBox.goToCase("EMPTY");
        }
        if (this.bonusSelector) {
            this.bonusSelector.goToCase("EMPTY");
        }
        this._winAnimator.currentSymbols.forEach((reel) => {
            reel.forEach((symbol) => {
                symbol.visible = true;
            });
        });
        if (ModuleManager.freespinsEnabled()) {
            this.fsMessage.visible = false;
            this.renderable = false;
        }
    }

    onLayoutChange(isPortrait: boolean): void {
        if (this.groupTweensPortrait && this.groupTweens) {
            if (isPortrait) {
                this.groupTweensPortrait.goToCase(
                    ModuleManager.freespinsActive() || ModuleManager.gambleActive() ? "GAMBLE" : "BASE",
                );
            } else {
                this.groupTweens.goToCase(
                    ModuleManager.freespinsActive() || ModuleManager.gambleActive() ? "GAMBLE" : "BASE",
                );
            }
        }
        super.onLayoutChange(isPortrait);
        const bg = this.childs["Background"];
        if (!bg) {
            return;
        }
        if (isPortrait) {
            bg.scale.x = LayoutManager.HeightScale * 1.7;
            bg.scale.y = LayoutManager.HeightScale * 1.7;
        }
    }

    showFS() {
        this.fsMessage.play(GameManager.freespinsResponse.freeSpins.totalCount);
        this.fsMessage.visible = true;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/